import React from "react";
import type { MarkdownProps } from "../types";
import { compileMarkdown } from "./MarkdownCompiler";

/**
 * Primary Markdown component for rendering markdown content
 *
 * @example
 * ```tsx
 * <Markdown>
 *   # Hello World
 *   This is **bold** and this is *italic*.
 * </Markdown>
 * ```
 *
 * @example With custom overrides
 * ```tsx
 * <Markdown
 *   overrides={{
 *     h1: ({ children }) => <h1 className="custom-heading">{children}</h1>
 *   }}
 * >
 *   # Custom Heading
 * </Markdown>
 * ```
 *
 * @example With custom styles
 * ```tsx
 * <Markdown
 *   markdownStyles={{
 *     h2: 'text-2xl md:text-4xl text-primary',
 *     img: 'shadow-lg rounded-2xl',
 *     iframe: 'aspect-video w-full rounded-2xl'
 *   }}
 * >
 *   ## Styled Heading
 *   ![Image](url)
 * </Markdown>
 * ```
 *
 * @example With heading IDs
 * ```tsx
 * <Markdown>
 *   ## Introduction {#intro}
 *   Content here...
 * </Markdown>
 * ```
 */
export function Markdown({
  children,
  className,
  overrides,
  markdownStyles,
  optixFlowConfig,
  useDefaults = true,
  wrapper = "div",
  widthGuard = true,
  ...options
}: MarkdownProps) {
  // Memoize the compiled output to avoid re-compilation on every render
  const compiled = React.useMemo(
    () =>
      compileMarkdown(children, {
        overrides,
        markdownStyles,
        optixFlowConfig,
        useDefaults,
        wrapper,
        widthGuard,
        ...options,
      }),
    [
      children,
      overrides,
      markdownStyles,
      optixFlowConfig,
      useDefaults,
      wrapper,
      widthGuard,
      options,
    ]
  );

  // Apply className if provided and wrapper is a string and result is a ReactElement.
  //
  // The mobile width guard is NOT applied here - it is applied inside
  // compileMarkdown so that direct compiler callers (customer-sites' block
  // registry, useMarkdown) get it too. See utils/width-guard.
  if (className && typeof wrapper === "string" && React.isValidElement(compiled)) {
    return React.cloneElement(compiled, {
      className: compiled.props.className
        ? `${compiled.props.className} ${className}`
        : className,
    } as any);
  }

  return <>{compiled}</>;
}

// Display name for debugging
Markdown.displayName = "Markdown";
